// Browser polyfills
import '@babel/polyfill';
import 'whatwg-fetch';

// Libs
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider, connect } from 'react-redux';

// Redux
import AppStore from './app-store';
import { getCompany } from './app-dux/company-dux';
import { languageChangeEvent, getString } from '@nackle/intl-tools';
import { getContent, updateLocale, getAllLocales } from './app-dux/app-dux.js';
import { applyRequestMiddleware } from './utils/fetch.js';
import { addCMXScript } from 'endpoints';

// Routing
import { Route, routeHistory } from '@nackle/biw-web-router';
import { routes, getMiddleware as getRouteMiddleware } from './app-routes.js';
import LoadingOverlay from './components/shared/loading-overlay/loading-overlay.jsx';
import Footer from './components/shared/footer';
import ErrorDisplay from './components/shared/error-display/error-display.jsx';

const getLocaleCode = ( state, props ) => {
	const {
		locale,
		updateLocale,
		defaultLocale
	} = props;

	let userSessionLocale = '';

	if ( sessionStorage.getItem( 'userSessionLocale' ) !== null ) {
		userSessionLocale = sessionStorage.getItem( 'userSessionLocale' );

		if ( userSessionLocale !== locale ) {
			updateLocale( userSessionLocale );
		}
	}

	return (
		userSessionLocale ||                                 // First try to use locale from use's session
		locale ||                                           // fall back to use the user selected locale
		( defaultLocale ? defaultLocale : null ) ||        // Then attempt to use the company default.
		'en-US'                                           // fall back to en-US
	);
};

applyRequestMiddleware( function( url, options ) {
    options.headers = {
        ...( options.headers || {} )
    };
    options.headers[ 'x-company-identifier' ] = `${ window.location.protocol }//${ window.location.hostname }`;
} );
/*******************************************************************************
 *  1. App (root level component)
 */

class App extends React.Component {
	state = {
		contentFetched: false,
		contentFetchInitiated: false,
		contentFetchLocaleCode: ''
	};

	componentDidMount() {
		addCMXScript();
		this.props.getCompany();
		this.props.getAllLocales();

		languageChangeEvent.on( ( data ) => {

			this.setState( {
				contentFetched: true,
				contentFetchLocaleCode: data.localeCode,
				properties: data.bundle
			} );
		} );
	}

	static getDerivedStateFromProps( props, state ) {
		const localeCode = getLocaleCode( state, props );
		if ( ( state.contentFetched === false && state.contentFetchInitiated === false && !props.companyLoading ) || localeCode !== state.contentFetchLocaleCode ) {
			props.getContent( localeCode ).catch( error => {
				if ( location.pathname !== '/error' ) {
					window.location.replace( '/error' );
				}
			} );
			state.contentFetchInitiated = true;
			state.contentFetchLocaleCode = localeCode;
		}

		if ( state.contentFetched && Object.keys( state.properties ).length === 0 && location.pathname !== '/error' ) {
			window.location.replace( '/error' );
		}

		return state;
	}

	setPageTitle = ( route ) => {

		const translatedTitle = ( route.match && route.match.title ) ? getString( route.match.title ) : getString( 'giftCode.pageTitle.default' );
		if ( translatedTitle.indexOf( 'Missing locale value' ) !== 0 ) {
			document.title = translatedTitle;
		}
		else {
			document.title = 'My Award Code';
		}
	};

	render() {
		const loading = this.props.contentLoading || this.props.loading || this.props.companyLoading;
		const { contentFetched } = this.state;

		return (
			<ErrorDisplay>
					<Route render={ ( route ) => {
						this.setPageTitle( route );
						return (
							<div className="app-container">
								<div className="main-container" role="main">
									{ route.pathname !== '/error' && <LoadingOverlay loading={ loading } /> }
									{
										route.match ?
											( contentFetched || route.pathname === '/error' ) &&
												<div className="container">
													{ route.component }
												</div>
											:
											route.pathname && <ErrorDisplay error="path"/>
									}
								</div>
								<Footer loading={ contentFetched }/>
							</div>
						);
					} }/>
			</ErrorDisplay>
		);
	}
}

// import styles after antd styles
import './styles/style.less';

/*******************************************************************************
 *  2. Connect App component to the Redux store
 */

App = connect(
	// mapStateToProps
	( state ) => {
		return {
			...state,
			loading: state.app.loading,
			companyLoaded: !!state.company.id,
			contentLoading: state.app.contentLoading,
			companyLoading: state.company.companyLoading,
			locale: state.app.locale,
			defaultLocale: state.company.defaultLocaleCode
		};
	},
	// mapDispatchToProps
	{
		getCompany,
		getContent,
		updateLocale,
		getAllLocales
	}
)( App );

/*******************************************************************************
 *  3. Connect Routing
 */
routeHistory.applyRoutes( routes );
routeHistory.applyMiddleware( getRouteMiddleware( AppStore ) );

/*******************************************************************************
 * 4. Render the the app
 */

const container = document.getElementById( 'app-root' );
const root = createRoot( container );

root.render( <Provider store={ AppStore }> <App /> </Provider> );
